/**
 * CommandPalette — global ⌘K / Ctrl+K launcher.
 *
 * Uses the shadcn/cmdk `CommandDialog` primitive.  Registers a document-level
 * keydown listener so the palette can be summoned from any member page.
 *
 * Actions are organised by group: Navigate, Calculators, Learning, Community,
 * Admin (visible only when the current user has role='admin').
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './ui/command';
import {
  LayoutDashboard, BookOpen, Bot, Calculator, ChefHat, Beaker, Video, Users,
  Store as StoreIcon, User as UserIcon, Award, Building2, Briefcase,
  Home as HomeIcon, ShieldCheck, LineChart, Settings, LogOut, Sparkles,
  DatabaseZap, FileClock, Bell, Megaphone, Calendar,
  MessagesSquare, Send, Ticket,
} from 'lucide-react';
import { AuthContext } from '../App';

// Static registry of destinations.  Grouped for a tidy palette.
const NAV_ITEMS = [
  { group: 'Navigate', label: 'Dashboard',            path: '/dashboard',            icon: LayoutDashboard, hint: 'Home base' },
  { group: 'Navigate', label: 'My Portal',            path: '/portal',               icon: UserIcon,        hint: 'Profile · activity · billing' },
  { group: 'Navigate', label: 'Directory',            path: '/directory',            icon: Building2 },
  { group: 'Navigate', label: 'Partners',             path: '/partners',             icon: Users },
  { group: 'Navigate', label: 'Store',                path: '/store',                icon: StoreIcon },
  { group: 'Navigate', label: 'Notifications',        path: '/notifications',        icon: Bell,           hint: 'Your inbox' },
  { group: 'Navigate', label: 'Pricing',              path: '/pricing',              icon: Award },
  { group: 'Navigate', label: 'Consulting',           path: '/consulting',           icon: Briefcase },

  { group: 'Learning', label: 'Learning Catalog',     path: '/learning',             icon: BookOpen,       hint: 'Courses & modules' },
  { group: 'Learning', label: 'Video Library',        path: '/videos',               icon: Video },
  { group: 'Learning', label: 'AI Assistant',         path: '/ai-chat',              icon: Bot,            hint: 'Ask anything' },

  { group: 'Calculators & Tools', label: 'All Calculators',     path: '/calculators',          icon: Calculator },
  { group: 'Calculators & Tools', label: 'Business Calculator', path: '/business',             icon: LineChart },
  { group: 'Calculators & Tools', label: 'Household Calculator', path: '/household-calculator', icon: HomeIcon },
  { group: 'Calculators & Tools', label: 'Recipe Builder',      path: '/recipes',              icon: ChefHat },
  { group: 'Calculators & Tools', label: 'Freeze Dry Database', path: '/database',             icon: DatabaseZap },
  { group: 'Calculators & Tools', label: 'Batch Tracker',       path: '/batch-tracker',        icon: Beaker },
  { group: 'Calculators & Tools', label: 'Batch History',       path: '/batch-history',        icon: FileClock },

  { group: 'Community', label: 'Community Forum',     path: '/forum',                icon: Users },
];

const ADMIN_ITEMS = [
  { group: 'Admin', label: 'Admin Dashboard',   path: '/admin',            icon: ShieldCheck },
  { group: 'Admin', label: 'Admin Portal',      path: '/admin/portal',     icon: Settings },
  { group: 'Admin', label: 'Mod Room',          path: '/admin/mod-room',   icon: MessagesSquare, hint: 'Staff-only discussion' },
  { group: 'Admin', label: 'User Management',   path: '/admin/users',      icon: Users },
  { group: 'Admin', label: 'Invite admins',     path: '/admin/invites',    icon: Send, hint: 'Send admin/moderator invite links' },
  { group: 'Admin', label: 'Certificates',      path: '/admin/certificates', icon: Award },
  { group: 'Admin', label: 'Access codes',      path: '/admin/codes',      icon: Ticket },
  { group: 'Admin', label: 'LMS Admin',         path: '/admin/lms',        icon: BookOpen },
  { group: 'Admin', label: 'Analytics',         path: '/admin/analytics',  icon: LineChart },
  { group: 'Admin', label: 'User Management',   path: '/admin/users',      icon: Users },
  { group: 'Admin', label: 'Directory Admin',   path: '/admin/directory',  icon: Building2 },
  { group: 'Admin', label: 'Send Broadcast',    path: '/admin/broadcast',  icon: Megaphone,      hint: 'Compose an announcement' },
  { group: 'Admin', label: "What's New",        path: '/admin/whats-new',  icon: Megaphone,      hint: 'Landing-page release notes' },
  { group: 'Admin', label: 'Manage Events',     path: '/admin/events',     icon: Calendar,       hint: 'Landing-page events + RSVPs' },
];

const groupItems = (items) => {
  const out = {};
  items.forEach((it) => {
    if (!out[it.group]) out[it.group] = [];
    out[it.group].push(it);
  });
  return out;
};

const CommandPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const grouped = groupItems([...NAV_ITEMS, ...(isAdmin ? ADMIN_ITEMS : [])]);

  const run = (fn) => {
    onOpenChange(false);
    // small tick so the dialog can fully unmount before we route
    setTimeout(fn, 0);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Type a page, tool or action…"
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>No matching pages.</CommandEmpty>

        {Object.entries(grouped).map(([group, items], idx) => (
          <React.Fragment key={group}>
            {idx > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {items.map(({ label, path, icon: Icon, hint }) => (
                <CommandItem
                  key={path}
                  value={`${label} ${path} ${hint || ''}`}
                  onSelect={() => run(() => navigate(path))}
                  data-testid={`cmdk-item-${path.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`}
                >
                  <Icon className="text-muted-foreground" />
                  <span className="font-semibold">{label}</span>
                  {hint && <span className="ml-2 text-xs text-muted-foreground">{hint}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </React.Fragment>
        ))}

        <CommandSeparator />
        <CommandGroup heading="Session">
          <CommandItem
            value="tour restart walkthrough"
            onSelect={() => run(() => {
              try { window.localStorage.removeItem('dashboard_tour_seen_v1'); } catch (_) { /* ignore */ }
              navigate('/dashboard');
              setTimeout(() => window.location.reload(), 100);
            })}
            data-testid="cmdk-item-restart-tour"
          >
            <Sparkles className="text-muted-foreground" />
            <span className="font-semibold">Restart dashboard tour</span>
          </CommandItem>
          <CommandItem
            value="theme toggle appearance"
            onSelect={() => run(() => {
              const t = document.documentElement;
              t.classList.toggle('dark');
            })}
            data-testid="cmdk-item-toggle-theme"
          >
            <Sparkles className="text-muted-foreground" />
            <span className="font-semibold">Toggle theme</span>
            <CommandShortcut>light / dark</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="sign out logout"
            onSelect={() => run(() => { logout && logout(); navigate('/'); })}
            data-testid="cmdk-item-signout"
          >
            <LogOut className="text-muted-foreground" />
            <span className="font-semibold">Sign out</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

/**
 * Global context — a single shared command-palette state so any part of the
 * app (custom headers on Dashboard/LandingPage, MemberShell nav, etc.) can
 * open the same palette instance.
 */
const CommandPaletteContext = React.createContext({
  open: false,
  setOpen: () => {},
});

/**
 * CommandPaletteProvider — mounted once in App.js. Owns the palette state,
 * wires the ⌘K / Ctrl+K global listener and renders exactly one palette.
 */
export const CommandPaletteProvider = ({ children }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      if (isK && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <CommandPaletteContext.Provider value={{ open, setOpen }}>
      {children}
      <CommandPalette open={open} onOpenChange={setOpen} />
    </CommandPaletteContext.Provider>
  );
};

/**
 * useCommandPalette — subscribe to the global palette state.  Kept as the
 * default hook name so MemberShell + any future header can call it.
 */
export const useCommandPalette = () => useContext(CommandPaletteContext);

export default CommandPalette;
